import React, { forwardRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useFormik } from "formik";
import {
  User,
  Mail,
  Phone,
  ChevronDown,
  Settings,
  Calendar,
  Handshake,
  Headphones,
  Briefcase,
  PhoneIcon,
  Clipboard,
} from "lucide-react";
import toast from "react-hot-toast";
import ButtonPrimary from "../Button/ButtonPrimary";
import { IContactFormProps, IFormData } from "@/Interfaces/interface.form";
import { submitEnquiry } from "@/services/formService";
import { formValidations } from "@/utils/formValidations";
import {
  formItemVariants,
  formParentAnimation,
} from "@/animation/formAnimation";
import { environment } from "@/environments/environment";

//   type options
const enquiryTypes = [
  {
    value: "bulk-order",
    label: "Bulk Order / Product Enquiry",
    icon: <PhoneIcon className="h-5 w-5 text-blue-700" />,
  },
  {
    value: "customization",
    label: "Customization Request",
    icon: <Settings className="h-5 w-5 text-blue-700" />,
  },
  {
    value: "meeting",
    label: "Schedule a Meeting / Demo",
    icon: <Calendar className="h-5 w-5 text-blue-700" />,
  },
  {
    value: "partnership",
    label: "Partnership / Collaboration",
    icon: <Handshake className="h-5 w-5 text-blue-700" />,
  },
  {
    value: "support",
    label: "Support / Complaint",
    icon: <Headphones className="h-5 w-5 text-blue-700" />,
  },
  {
    value: "job-enquiry",
    label: "Job Enquiry",
    icon: <Briefcase className="h-5 w-5 text-blue-700" />,
  },
];

interface Props {
  value: string;
  onChange: (val: string) => void;
  error?: string;
  disabled?: boolean;
  onBlur?: () => void;
}

const EnquiryDropdown: React.FC<Props> = ({
  value,
  onChange,
  error,
  disabled,
  onBlur,
}) => {
  const [open, setOpen] = useState(false);

  const selected = enquiryTypes.find((t) => t.value === value);

  return (
    <div className="relative">
      <label className="block text-sm font-medium  mb-2 font-body text-left">
        Enquiry Type *
      </label>
      <button
        type="button"
        disabled={disabled}
        className={`w-full flex items-center justify-between pl-2.5 pr-4 py-3 border-[2.5px] rounded-lg text-left transition-colors h-[52px] ${error
          ? "border-red-300 bg-red-50"
          : "border-gray-100 hover:border-blue-700"
          } disabled:opacity-50 disabled:pointer-events-none focus:border-primary-400`}
        onClick={() => setOpen(!open)}
        onBlur={onBlur}
      >
        <span
          className={`flex items-center gap-2 font-body ${selected ? "text-gray-800 text-sm" : "text-neutral-400"
            }`}
        >
          {selected ? (
            selected.icon
          ) : (
            <Clipboard className="h-5 w-5 text-blue-700" />
          )}
          {selected ? selected.label : "Select enquiry type"}
        </span>
        <motion.span
          animate={{ rotate: open ? 180 : 0 }}
          transition={{ duration: 0.2 }}
        >
          <ChevronDown className="h-5 w-5 text-neutral-400" />
        </motion.span>
      </button>

      <AnimatePresence>
        {open && (
          <motion.ul
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            className="absolute z-10 mt-2 w-full bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden"
          >
            {enquiryTypes.map((type) => (
              <li key={type.value}>
                <button
                  type="button"
                  className="flex items-center gap-4 w-full px-4 py-2 text-left hover:bg-gray-50"
                  onClick={() => {
                    onChange(type.value);
                    setOpen(false);
                  }}
                >
                  {type.icon}
                  {type.label}
                </button>
              </li>
            ))}
          </motion.ul>
        )}
      </AnimatePresence>

      {error && <p className="mt-1 text-sm text-red-600 text-left">{error}</p>}
    </div>
  );
};

const ContactForm = forwardRef<HTMLDivElement, IContactFormProps>(
  (props, ref) => {
    const { showField = true } = props;
    const platformName = environment.VITE_APP_APPLICATION_NAME;

    const submitForm = async (
      values: IFormData,
      platform: string,
      resetForm: any
    ) => {
      try {
        await submitEnquiry(values, platform);
        toast.success(
          "Enquiry submitted successfully. Our team will get back to you soon."
        );
        resetForm();
      } catch (error: any) {
        console.error(error);
      }
    };

    // Formik setup with Yup validation
    const formik = useFormik<IFormData>({
      initialValues: {
        name: "",
        email: "",
        contactNumber: "",
        enquiryType: showField ? "" : "Schedule a Meeting / Demo",
        message: "",
      },
      validationSchema: formValidations,
      onSubmit: async (values, { resetForm }) =>
        submitForm(values, platformName, resetForm),
    });

    return (
      <div
        ref={ref}
        className="py-8 sm:py-10 px-4 sm:px-6 lg:px-10 max-w-4xl mx-auto"
      >
        <motion.div
          className="flex flex-col gap-5 justify-center items-center"
          {...formParentAnimation}
        >
          {/* Form */}
          <motion.div
            {...formItemVariants}
            className="bg-white rounded-2xl shadow-xl border border-gray-100 w-full"
          >
            <div className="p-6 sm:p-8 my-4 space-y-6">
              <form onSubmit={formik.handleSubmit}>
                {/* Name Field */}
                <motion.div {...formItemVariants}>
                  <label
                    htmlFor="name"
                    className="block text-sm font-medium  mb-2 font-body text-left"
                  >
                    Full Name *
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <User className="h-5 w-5 text-blue-700" />
                    </div>
                    <input
                      type="text"
                      id="name"
                      disabled={formik.isSubmitting}
                      {...formik.getFieldProps("name")}
                      className={`block w-full pl-10 pr-3 py-3 border-[2.5px] rounded-lg focus:ring-[2.5px] focus:outline-none focus:ring-primary focus:border-transparent transition-colors placeholder:text-neutral-400 disabled:opacity-50 disabled:pointer-events-none ${formik.touched.name && formik.errors.name
                        ? "border-red-300 bg-red-50"
                        : "border-gray-100 hover:border-blue-700"
                        }`}
                      placeholder="Enter your full name"
                    />
                  </div>
                  {formik.touched.name && formik.errors.name && (
                    <motion.p
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="mt-1 text-sm text-red-600 text-left"
                    >
                      {formik.errors.name}
                    </motion.p>
                  )}
                </motion.div>

                <div className="grid gap-6 mt-6 md:grid-cols-2">
                  {/* Email Field */}
                  <motion.div
                    {...formItemVariants}
                    className={`${showField ? "md:col-span-2" : "col-span-1"}`}
                  >
                    <label
                      htmlFor="email"
                      className="block text-sm font-medium  mb-2 font-body text-left"
                    >
                      Email Address *
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Mail className="h-5 w-5 text-blue-700" />
                      </div>
                      <input
                        type="email"
                        id="email"
                        disabled={formik.isSubmitting}
                        {...formik.getFieldProps("email")}
                        className={`block w-full pl-10 pr-3 py-3 border-[2.5px] rounded-lg focus:ring-[2.5px] focus:outline-none focus:ring-primary focus:border-transparent transition-colors placeholder:text-neutral-400 disabled:opacity-50 disabled:pointer-events-none ${formik.touched.email && formik.errors.email
                          ? "border-red-300 bg-red-50"
                          : "border-gray-100 hover:border-blue-700"
                          }`}
                        placeholder="Enter your email address"
                      />
                    </div>
                    {formik.touched.email && formik.errors.email && (
                      <motion.p
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="mt-1 text-sm text-red-600 text-left"
                      >
                        {formik.errors.email}
                      </motion.p>
                    )}
                  </motion.div>

                  {/* Contact Number Field */}
                  <motion.div {...formItemVariants}>
                    <label
                      htmlFor="contactNumber"
                      className="block text-sm font-medium  mb-2 font-body text-left"
                    >
                      Contact Number *
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Phone className="h-5 w-5 text-blue-700" />
                      </div>
                      <input
                        type="tel"
                        id="contactNumber"
                        disabled={formik.isSubmitting}
                        {...formik.getFieldProps("contactNumber")}
                        className={`block w-full pl-10 pr-3 py-3 border-[2.5px] rounded-lg focus:ring-[2.5px] focus:outline-none focus:ring-primary focus:border-transparent transition-colors placeholder:text-neutral-400 disabled:opacity-50 disabled:pointer-events-none ${formik.touched.contactNumber &&
                          formik.errors.contactNumber
                          ? "border-red-300 bg-red-50"
                          : "border-gray-100 hover:border-blue-700"
                          }`}
                        placeholder="Enter your contact number"
                      />
                    </div>
                    {formik.touched.contactNumber &&
                      formik.errors.contactNumber && (
                        <motion.p
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="mt-1 text-sm text-red-600 text-left"
                        >
                          {formik.errors.contactNumber}
                        </motion.p>
                      )}
                  </motion.div>

                  {/* Enquiry Dropdown (only if showField is true) */}
                  {showField && (
                    <motion.div {...formItemVariants}>
                      <EnquiryDropdown
                        value={formik.values.enquiryType}
                        onChange={(val) =>
                          formik.setFieldValue("enquiryType", val)
                        }
                        onBlur={() =>
                          formik.setFieldTouched("enquiryType", true)
                        }
                        error={
                          formik.touched.enquiryType
                            ? formik.errors.enquiryType
                            : undefined
                        }
                        disabled={formik.isSubmitting}
                      />
                    </motion.div>
                  )}
                </div>

                {/* Message Field */}
                <motion.div {...formItemVariants} className="mt-6">
                  <label
                    htmlFor="message"
                    className="block text-sm font-medium  mb-2 font-body text-left"
                  >
                    Message *
                  </label>
                  <textarea
                    id="message"
                    rows={5}
                    disabled={formik.isSubmitting}
                    {...formik.getFieldProps("message")}
                    className={`block w-full p-3 border-[2.5px] rounded-lg focus:ring-[2.5px] focus:outline-none focus:ring-primary focus:border-transparent transition-colors placeholder:text-neutral-400 disabled:opacity-50 disabled:pointer-events-none ${formik.touched.message && formik.errors.message
                      ? "border-red-300 bg-red-50"
                      : "border-gray-100 hover:border-blue-700"
                      }`}
                    placeholder="Tell us more about your requirements..."
                  />
                  <div className="flex flex-col-reverse md:flex-row justify-between items-baseline mt-1 gap-2 md:gap-10">
                    {formik.touched.message && formik.errors.message ? (
                      <motion.p
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-sm text-red-600 text-left flex flex-1"
                      >
                        {formik.errors.message}
                      </motion.p>
                    ) : (
                      <div />
                    )}
                    <p
                      className={`text-xs font-body text-right ${formik.values.message.replace(/\s/g, "").length < 20 &&
                        formik.values.message.replace(/\s/g, "").length !== 0
                        ? "text-red-600"
                        : "text-neutral-400"
                        }`}
                    >
                      {formik.values.message.replace(/\s/g, "").length}/1000
                      characters
                    </p>
                  </div>
                </motion.div>

                {/* Submit Button */}
                <motion.div {...formItemVariants} className="pt-4 text-end">
                  <ButtonPrimary
                    type="submit"
                    loading={formik.isSubmitting}
                    disabled={formik.isSubmitting || !formik.isValid}
                  >
                    Submit
                  </ButtonPrimary>
                </motion.div>
              </form>
            </div>
          </motion.div>
        </motion.div>
      </div>
    );
  }
);
export default ContactForm;
